import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Trans, useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import { useNavigate } from "react-router-dom";

interface PinVerificationProps {
  onPinVerified?: (pin: string) => void;
}

export interface PinVerificationRef {
  verifyPin: () => Promise<boolean>;
  isPinComplete: () => boolean;
  verificationPin: string;
}

const PinVerification = forwardRef<PinVerificationRef, PinVerificationProps>(
  ({ onPinVerified }, ref) => {
    const navigate = useNavigate();;
    const { t } = useTranslation();

    const [pin, setPin] = useState("");
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const handleContactSupport = () => {
      // dispatch(bookingCreationCompleted());
      navigate("/hints/page/HelpAccordionList");
    };

    const handlePinChange = (value: string) => {
      setPin(value);
      if (hasError) {
        setHasError(false);
        setErrorMessage("");
      }
    };

    const verifyPin = async (): Promise<boolean> => {
      return true;
    };

    const handleCloseError = () => {
      setHasError(false);
      setErrorMessage("");
    };

    // Expose the verifyPin function to the parent component
    useImperativeHandle(ref, () => ({
      verifyPin,
      isPinComplete: () => pin.length === 6,
      setPin: (pin: string) => setPin(pin),
      verificationPin: pin,
    }));

    return (
      <div className="flex flex-col justify-between items-center p-4 box-border h-full">
        <div className="flex flex-col items-center text-center flex-1 justify-center">
          {hasError && (
            <div className="bg-red-500 text-white p-3 rounded-lg mb-4 flex items-center justify-between w-full max-w-md">
              <p className="font-ibm-plex-sans font-normal text-sm leading-5">
                {errorMessage}
              </p>
              <button
                onClick={handleCloseError}
                className="bg-transparent border-none text-white cursor-pointer text-base font-bold"
              >
                ✕
              </button>
            </div>
          )}

          <OtpInput
            value={pin}
            onChange={handlePinChange}
            numInputs={6}
            renderInput={(props: any, index: number) => {
              const isFilled = pin.length > index;
              return (
                <input
                  {...props}
                  type="text"
                  inputMode="text"
                  className={`
                  !w-12 !h-12 mx-1 text-xl text-black text-center border-2 rounded-lg
                  ${
                    hasError
                      ? "border-red-500 bg-red-50"
                      : isFilled
                      ? "border-primary bg-primary/10"
                      : "border-gray-300"
                  }
                  focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
                `}
                />
              );
            }}
          />

          <p className="font-ibm-plex-sans font-normal text-base leading-6 text-center mt-6 mb-8 text-gray-800 max-w-xs">
            <p style={{ margin: 0 }}>
              <Trans
                i18nKey={
                  "book_location.parcel_placement.pin_verification.description"
                }
                components={{
                  bold: (
                    <span
                      style={{
                        fontWeight: "bold",
                      }}
                    ></span>
                  ),
                }}
              />
            </p>
          </p>
          <div className="text-center pb-5 px-5">
            <h2 className="text-gray-500 text-base font-normal">
              <Trans
                i18nKey={"itemRetrieval.successMessage.help"}
                components={{
                  contactSupport: (
                    <span
                      className="text-primary cursor-pointer"
                      onClick={handleContactSupport}
                    >
                      {t(
                        "book_location.success.contact_support",
                        "Contact support"
                      )}
                    </span>
                  ),
                }}
              />
            </h2>
          </div>
        </div>
      </div>
    );
  }
);

PinVerification.displayName = "PinVerification";

export default PinVerification;
